import React, { useState, useEffect, useCallback } from 'react';
import { Drawer, Form, Button, Space, Typography, Divider, Row, Col, Input, Tooltip } from 'antd';
import { DeleteOutlined, EditOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { NODE_DEFINITIONS } from '../../utils/nodeTypes';
import {
  InputNodeProperties,
  DatasetNodeProperties,
  FilterNodeProperties,
  CalculationNodeProperties,
  LogicNodeProperties,
  OutputNodeProperties,
  AddColumnNodeProperties,
  RemoveColumnNodeProperties,
  RenameColumnNodeProperties,
  MapNodeProperties,
  ReduceNodeProperties,
  SliceNodeProperties,
  DistinctNodeProperties,
  PivotNodeProperties,
  JoinNodeProperties,
  MergeNodeProperties,
  WindowFunctionNodeProperties,
  AggregationNodeProperties,
  SwitchCaseNodeProperties,
  MergeFlowNodeProperties,
  LoopNodeProperties,
  WaitNodeProperties,
  CsvWriterNodeProperties,
  CsvInputNodeProperties,
  ExcelInputNodeProperties,
  TableNodeProperties,
  ExportExcelNodeProperties,
  SetDatasetNameNodeProperties,
  // Batch node properties
  BatchStartNodeProperties,
  BatchEndNodeProperties,
  // Decoration node properties
  CommentNodeProperties,
  TextAnnotationNodeProperties,
  ShapeRectangleNodeProperties
} from './NodeProperties';
import { useNodeSchema } from './hooks/useNodeSchema';

const { Title, Text } = Typography;

// Debounce utility for auto-save
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const PropertyDrawer = ({ 
  visible, 
  onClose, 
  selectedNode, 
  onUpdateNode, 
  onDeleteNode,
  isFixedPanel = false,
  nodes = [],
  edges = []
}) => {
  const [form] = Form.useForm();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Calculate input schema dynamically
  const calculatedSchema = useNodeSchema(selectedNode?.id, nodes, edges);
  // Use calculated schema, fallback to stored schema (for backward compatibility)
  const inputSchema = calculatedSchema && calculatedSchema.length > 0 ? calculatedSchema : (selectedNode?.data?.inputSchema || []);

  const [editingNodeName, setEditingNodeName] = useState(false);
  const [nodeName, setNodeName] = useState('');
  const [displayNodeName, setDisplayNodeName] = useState(''); // For immediate UI updates

  // Debounced auto-save function
  const debouncedAutoSave = useCallback(
    debounce((nodeId, newData) => {
      console.log('Auto-saving node configuration:', nodeId, newData);
      onUpdateNode(nodeId, newData);
      setHasUnsavedChanges(false);
    }, 500),
    [onUpdateNode]
  );

  // Sync batch nodes - update batch_end outputs when batch_start inputs change
  const syncBatchNodes = useCallback(() => {
    if (!selectedNode) return;
    
    const nodeType = selectedNode.data?.nodeType || selectedNode.data?.type;
    if (nodeType !== 'batch_start') return;
    
    // Find paired batch_end and update its outputs
    const batchStartId = selectedNode.id;
    const batchStartConfig = selectedNode.data?.config || {};
    const dynamicInputs = batchStartConfig.dynamicInputs || [];
    
    // BFS to find paired batch_end
    const visited = new Set();
    const queue = [batchStartId];
    let batchEndNode = null;
    
    while (queue.length > 0) {
      const currentId = queue.shift();
      if (visited.has(currentId)) continue;
      visited.add(currentId);
      
      const node = nodes.find(n => n.id === currentId);
      const nType = node?.data?.nodeType || node?.data?.type;
      
      if (nType === 'batch_end' && currentId !== batchStartId) {
        batchEndNode = node;
        break;
      }
      
      // Add downstream nodes
      edges.filter(e => e.source === currentId).forEach(e => {
        queue.push(e.target);
      });
    }
    
    if (!batchEndNode) return;
    
    // Create matching outputs for batch_end
    const dynamicOutputs = dynamicInputs.map((input, idx) => ({
      id: `output_${idx + 1}`,
      name: input.name || `output_${idx + 1}`
    }));
    
    // If no outputs, keep at least one default
    if (dynamicOutputs.length === 0) {
      dynamicOutputs.push({ id: 'output_1', name: 'output_1' });
    }
    
    // Update batch_end node immediately (no debounce)
    const updatedData = {
      ...batchEndNode.data,
      config: {
        ...batchEndNode.data.config,
        dynamicOutputs,
        pairedBatchStartId: batchStartId
      }
    };
    
    console.log('Syncing batch_end outputs:', batchEndNode.id, dynamicOutputs);
    onUpdateNode(batchEndNode.id, updatedData);
  }, [selectedNode, nodes, edges, onUpdateNode]);

  // Enhanced updateNodeData function with auto-save
  const updateNodeData = useCallback((newData) => {
    if (!selectedNode) return;
    
    console.log('Node data updated:', newData);
    setHasUnsavedChanges(true);
    
    // Merge with existing data to preserve structure
    const updatedData = {
      nodeType: selectedNode.data.nodeType,
      label: selectedNode.data.label,
      ...newData
    };
    
    // Trigger debounced auto-save
    debouncedAutoSave(selectedNode.id, updatedData);
    
    // If this is a batch_start node, sync batch_end immediately
    const nodeType = selectedNode.data?.nodeType || selectedNode.data?.type;
    if (nodeType === 'batch_start' && newData.config?.dynamicInputs) {
      // Use setTimeout to ensure the node update completes first
      setTimeout(() => syncBatchNodes(), 50);
    }
  }, [selectedNode, debouncedAutoSave, syncBatchNodes]);

  // Handle node name update
  const updateNodeName = useCallback((newName) => {
    if (!selectedNode) return;
    
    const updatedData = {
      ...selectedNode.data,
      customName: newName,
      label: newName || getDefaultNodeName(selectedNode.data?.nodeType)
    };
    
    setHasUnsavedChanges(true);
    
    // Force update display immediately for UI responsiveness
    const finalName = newName || getDefaultNodeName(selectedNode.data?.nodeType);
    setDisplayNodeName(finalName);
    
    debouncedAutoSave(selectedNode.id, updatedData);
  }, [selectedNode, debouncedAutoSave]);

  // Get default node name from NODE_DEFINITIONS
  const getDefaultNodeName = (nodeType) => {
    const nodeDef = NODE_DEFINITIONS[nodeType];
    return nodeDef?.name || nodeType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown Node';
  };

  // Get current node display name
  const getCurrentNodeName = () => {
    if (!selectedNode) return '';
    
    // During editing, use displayNodeName for immediate updates
    if (displayNodeName && !editingNodeName) {
      return displayNodeName;
    }
    
    return selectedNode.data?.customName || selectedNode.data?.label || getDefaultNodeName(selectedNode.data?.nodeType);
  };

  // Initialize node name when selected node changes
  useEffect(() => {
    if (selectedNode) {
      const currentName = getCurrentNodeName();
      setNodeName(currentName);
      setDisplayNodeName(''); // Reset display name
      setEditingNodeName(false);
    }
  }, [selectedNode]);

  // Sync node name when node data changes (after auto-save)
  useEffect(() => {
    if (selectedNode && !editingNodeName) {
      const currentName = selectedNode.data?.customName || selectedNode.data?.label || getDefaultNodeName(selectedNode.data?.nodeType);
      setNodeName(currentName);
      // Don't reset displayNodeName here to maintain immediate UI feedback
    }
  }, [selectedNode?.data?.customName, selectedNode?.data?.label, editingNodeName]);

  // Handle node deletion with confirmation
  const handleDelete = useCallback(() => {
    if (!selectedNode) return;
    
    // Simple confirmation - could be enhanced with Modal.confirm
    if (window.confirm(`Are you sure you want to delete the "${selectedNode.data.label || selectedNode.data.nodeType}" node?`)) {
      onDeleteNode(selectedNode.id);
      onClose();
    }
  }, [selectedNode, onDeleteNode, onClose]);

  // Early return if no selected node
  if (!selectedNode) {
    return null;
  }

  const nodeType = selectedNode.data?.nodeType || selectedNode.data?.type || 'unknown';
  const nodeDef = NODE_DEFINITIONS[nodeType] || {
    name: nodeType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    description: 'No definition found for this node type',
    inputs: [],
    outputs: []
  };

  // Handle node name editing
  const handleNodeNameEdit = () => {
    // Sync nodeName state with current actual name before editing
    setNodeName(getCurrentNodeName());
    setEditingNodeName(true);
  };

  const handleNodeNameSave = () => {
    updateNodeName(nodeName);
    setEditingNodeName(false);
  };

  const handleNodeNameCancel = () => {
    setNodeName(getCurrentNodeName());
    setDisplayNodeName(''); // Reset display name
    setEditingNodeName(false);
  };

  const handleNodeNameChange = (e) => {
    setNodeName(e.target.value);
  };

  const handleNodeNameKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleNodeNameSave();
    } else if (e.key === 'Escape') {
      handleNodeNameCancel();
    }
  };

  // Render node name with edit functionality
  const renderNodeNameEditor = (size = 4) => {
    if (editingNodeName) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <Input
            value={nodeName}
            onChange={handleNodeNameChange}
            onKeyDown={handleNodeNameKeyPress}
            placeholder="Enter node name"
            style={{ flex: 1, fontSize: size === 4 ? '16px' : '14px' }}
            autoFocus
          />
          <Button 
            type="text" 
            icon={<CheckOutlined />} 
            size="small"
            onClick={handleNodeNameSave}
            style={{ color: '#52c41a' }}
          />
          <Button 
            type="text" 
            icon={<CloseOutlined />} 
            size="small"
            onClick={handleNodeNameCancel}
            style={{ color: '#ff4d4f' }}
          />
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Title 
          level={size} 
          style={{ margin: 0, flex: 1 }}
          className="node-name-title"
        >
          {/* Always use getCurrentNodeName() for real-time updates */}
          {getCurrentNodeName()}
        </Title>
        <Button 
          type="text" 
          icon={<EditOutlined />} 
          size="small"
          onClick={handleNodeNameEdit}
          style={{ padding: '4px 8px' }}
          title="Click to edit node name"
        />
      </div>
    );
  };

  // Render configuration form based on node type
  const renderConfigForm = () => {
    const config = selectedNode.data?.config || {};
    
    try {
      switch (nodeType) {
        case 'input':
          return <InputNodeProperties config={config} form={form} updateConfig={updateNodeData} />;

        case 'csv_input':
          return <CsvInputNodeProperties config={config} form={form} updateConfig={updateNodeData} />;

        case 'excel_input':
          return <ExcelInputNodeProperties 
            data={selectedNode.data} 
            updateNodeData={updateNodeData}
          />;

        case 'table':
          return <TableNodeProperties 
            data={selectedNode.data} 
            updateNodeData={updateNodeData}
            nodes={nodes}
            edges={edges}
          />;


        case 'filter_rows':
        case 'if_else':
        case 'filter_columns':
          return <FilterNodeProperties 
            config={config} 
            nodeType={nodeType} 
            updateConfig={updateNodeData}
            inputSchema={inputSchema}
            updateNodeName={updateNodeName}
          />;

        case 'calculate_field':
        case 'sort':
          return <CalculationNodeProperties 
            config={config} 
            nodeType={nodeType} 
            updateConfig={updateNodeData} 
            inputSchema={inputSchema}
          />;

        case 'add_column':
          return <AddColumnNodeProperties 
            config={config} 
            form={form} 
            updateConfig={updateNodeData}
            inputSchema={inputSchema}
            updateNodeName={updateNodeName}
          />;

        case 'remove_column':
          return <RemoveColumnNodeProperties 
            config={config} 
            form={form} 
            updateConfig={updateNodeData} 
            inputSchema={inputSchema}
          />;

        case 'rename_column':
          return <RenameColumnNodeProperties 
            config={config} 
            form={form} 
            updateConfig={updateNodeData} 
            inputSchema={inputSchema}
          />;

        case 'range_branch':
          return <LogicNodeProperties 
            config={config} 
            nodeType={nodeType} 
            updateConfig={updateNodeData} 
            inputSchema={inputSchema}
          />;

        case 'dataset_output':
          return <OutputNodeProperties 
            config={config} 
            nodeType={nodeType} 
            updateConfig={updateNodeData} 
            inputSchema={inputSchema}
          />;

        // Transform nodes
        case 'map':
          return <MapNodeProperties 
            config={config} 
            updateConfig={updateNodeData} 
            inputSchema={inputSchema}
          />;
        
        case 'reduce':
          return <ReduceNodeProperties config={config} updateConfig={updateNodeData} inputSchema={inputSchema} />;
        
        case 'slice':
          return <SliceNodeProperties config={config} updateConfig={updateNodeData} inputSchema={inputSchema} />;
        
        case 'distinct':
          return <DistinctNodeProperties config={config} updateConfig={updateNodeData} inputSchema={inputSchema} />;

        case 'pivot':
          return <PivotNodeProperties config={config} updateConfig={updateNodeData} inputSchema={inputSchema} />;
        
        case 'join':
          return <JoinNodeProperties 
            config={config} 
            updateConfig={updateNodeData} 
            nodes={nodes}
            edges={edges}
            selectedNode={selectedNode}
            inputSchema={inputSchema}
          />;
        
        case 'merge':
          return <MergeNodeProperties config={config} updateConfig={updateNodeData} inputSchema={inputSchema} />;
        
        case 'window_function':
          return <WindowFunctionNodeProperties config={config} updateConfig={updateNodeData} inputSchema={inputSchema} />;
        
        case 'group_by':
          return <AggregationNodeProperties 
            config={config} 
            form={form} 
            updateConfig={updateNodeData} 
            inputSchema={inputSchema}
          />;

        // Control flow nodes
        case 'switch_case':
          return <SwitchCaseNodeProperties 
            config={config} 
            form={form} 
            updateConfig={updateNodeData} 
            inputSchema={inputSchema}
          />;
        
        case 'merge_flow':
          return <MergeFlowNodeProperties config={config} updateConfig={updateNodeData} inputSchema={inputSchema} />;
        
        case 'loop':
          return <LoopNodeProperties config={config} updateConfig={updateNodeData} inputSchema={inputSchema} />;
        
        case 'batch_start':
          return <BatchStartNodeProperties 
            config={config} 
            nodeId={selectedNode?.id}
            nodes={nodes}
            edges={edges}
            onConfigChange={updateNodeData}
            onSyncBatchNodes={syncBatchNodes}
          />;
        
        case 'batch_end':
          return <BatchEndNodeProperties 
            config={config} 
            nodeId={selectedNode?.id}
            nodes={nodes}
            edges={edges}
            onConfigChange={updateNodeData}
          />;
        
        case 'wait':
          return <WaitNodeProperties config={config} updateConfig={updateNodeData} inputSchema={inputSchema} />;

        case 'csv_writer':
          return <CsvWriterNodeProperties config={config} updateConfig={updateNodeData} inputSchema={inputSchema} />;

        case 'export_excel':
          return <ExportExcelNodeProperties 
            config={config} 
            updateConfig={updateNodeData} 
            nodes={nodes}
            edges={edges}
            selectedNode={selectedNode}
          />;

        case 'set_dataset_name':
          return <SetDatasetNameNodeProperties config={config} updateConfig={updateNodeData} />;

        // Decoration nodes - purely visual, no workflow effect
        case 'comment':
          return <CommentNodeProperties config={config} updateConfig={updateNodeData} />;
        
        case 'text_annotation':
          return <TextAnnotationNodeProperties config={config} updateConfig={updateNodeData} />;
        
        case 'shape_rectangle':
          return <ShapeRectangleNodeProperties config={config} updateConfig={updateNodeData} />;

        case 'start':
        case 'end':
          return (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Typography.Text type="secondary">
                This is a {nodeType} node. No additional configuration is required.
              </Typography.Text>
            </div>
          );

        default:
          return (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Typography.Text type="secondary">
                No configuration available for this node type: {nodeType}
              </Typography.Text>
              <div style={{ marginTop: 16 }}>
                <Typography.Text code>{JSON.stringify(config, null, 2)}</Typography.Text>
              </div>
            </div>
          );
      }
    } catch (error) {
      console.error('Error rendering config form:', error);
      return (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Typography.Text type="danger">
            Error loading configuration for this node type.
          </Typography.Text>
          <div style={{ marginTop: 8 }}>
            <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
              {error.message}
            </Typography.Text>
          </div>
        </div>
      );
    }
  };

  // Fixed panel layout
  if (isFixedPanel) {
    return (
      <div style={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        backgroundColor: '#fff'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px',
          borderBottom: '1px solid #f0f0f0',
          backgroundColor: '#fafafa'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              {/* Editable Node Name */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {renderNodeNameEditor(4)}
                {hasUnsavedChanges && (
                  <span style={{ 
                    color: '#faad14', 
                    fontSize: '12px', 
                    fontWeight: 400,
                    marginLeft: 8
                  }}>
                    (saving...)
                  </span>
                )}
              </div>
              
              {/* Node Type */}
              <Text type="secondary" style={{ fontSize: '12px', marginTop: 4, display: 'block' }}>
                Type: {nodeDef.name}
                {!editingNodeName && !selectedNode.data?.customName && (
                  <span style={{ color: '#1890ff', marginLeft: 8 }}>
                    (Click <EditOutlined /> to rename)
                  </span>
                )}
              </Text>
              
              <Text type="secondary" style={{ fontSize: '12px', marginTop: 2, display: 'block' }}>
                {nodeDef.description}
              </Text>
              <Text type="secondary" style={{ fontSize: '11px', color: '#8c8c8c' }}>
                ID: {selectedNode.id}
              </Text>
            </div>
            <Button 
              type="text" 
              onClick={onClose}
              style={{ padding: '4px 8px' }}
            >
              ✕
            </Button>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{
          padding: '12px 16px',
          borderBottom: '1px solid #f0f0f0',
          backgroundColor: '#fff'
        }}>
          <Space>
            <Button 
              danger 
              icon={<DeleteOutlined />} 
              onClick={handleDelete}
              size="small"
            >
              Delete Node
            </Button>
          </Space>
        </div>
        
        {/* Content */}
        <div style={{ 
          flex: 1, 
          padding: '16px',
          overflowY: 'auto'
        }}>
          <Form
            form={form}
            layout="vertical"
            onValuesChange={(changedValues, allValues) => {
              const currentConfig = selectedNode.data?.config || {};
              updateNodeData({ config: { ...currentConfig, ...allValues } });
            }}
          >
            {renderConfigForm()}
          </Form>
          
          <Divider />
          
          {/* Inputs/Outputs Info */}
          <div>
            <Title level={5}>Node Connections</Title>
            <Row gutter={16}>
              <Col span={12}>
                <Text strong>Inputs:</Text>
                <div style={{ marginTop: 4 }}>
                  {nodeDef.inputs.length ? (
                    nodeDef.inputs.map(input => (
                      <div key={input} style={{ fontSize: '12px', color: '#666' }}>
                        • {input}
                      </div>
                    ))
                  ) : (
                    <Text type="secondary" style={{ fontSize: '12px' }}>None</Text>
                  )}
                </div>
              </Col>
              <Col span={12}>
                <Text strong>Outputs:</Text>
                <div style={{ marginTop: 4 }}>
                  {nodeDef.outputs.length ? (
                    nodeDef.outputs.map(output => (
                      <div key={output} style={{ fontSize: '12px', color: '#666' }}>
                        • {output}
                      </div>
                    ))
                  ) : (
                    <Text type="secondary" style={{ fontSize: '12px' }}>None</Text>
                  )}
                </div>
              </Col>
            </Row>
          </div>
        </div>
      </div>
    );
  }

  // Original Drawer layout for backward compatibility
  return (
    <Drawer
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {renderNodeNameEditor(5)}
          {hasUnsavedChanges && (
            <span style={{ color: '#faad14', fontSize: '12px', marginLeft: 8 }}>
              (saving...)
            </span>
          )}
        </div>
      }
      placement="right"
      onClose={onClose}
      open={visible}
      width={500}
      zIndex={2000}
      mask={false}
      maskClosable={true}
      extra={
        <Space>
          <Button 
            danger 
            icon={<DeleteOutlined />} 
            onClick={handleDelete}
          >
            Delete
          </Button>
        </Space>
      }
    >
      <div style={{ marginBottom: 16 }}>
        <Text strong style={{ fontSize: '13px' }}>Node Type: {nodeDef.name}</Text>
        <br />
        <Text type="secondary">{nodeDef.description}</Text>
      </div>
      
      {/* Add some CSS for smooth editing experience */}
      <style jsx>{`
        .node-name-title:hover + button {
          opacity: 1;
        }
        .node-name-title + button {
          opacity: 0.5;
          transition: opacity 0.2s;
        }
        .node-name-title + button:hover {
          opacity: 1;
        }
      `}</style>
      
      <Divider />
      
      <Form
        form={form}
        layout="vertical"
      >
        {renderConfigForm()}
      </Form>
      
      <Divider />
      
      <div>
        <Title level={5}>Inputs/Outputs</Title>
        <Row gutter={16}>
          <Col span={12}>
            <Text strong>Inputs:</Text>
            <div>
              {nodeDef.inputs.length ? (
                nodeDef.inputs.map(input => (
                  <div key={input}>• {input}</div>
                ))
              ) : (
                <Text type="secondary">None</Text>
              )}
            </div>
          </Col>
          <Col span={12}>
            <Text strong>Outputs:</Text>
            <div>
              {nodeDef.outputs.length ? (
                nodeDef.outputs.map(output => (
                  <div key={output}>• {output}</div>
                ))
              ) : (
                <Text type="secondary">None</Text>
              )}
            </div>
          </Col>
        </Row>
      </div>
    </Drawer>
  );
};

export default PropertyDrawer;